<template>
  <div class="app">
    <!-- 登录页面 -->
    <router-view v-if="!isAuthenticated" />
    
    <!-- 主应用界面 -->
    <el-container v-else class="app-container">
      <!-- 顶部导航 -->
      <el-header class="app-header">
        <div class="header-content">
          <div class="logo">
            <el-icon><Money /></el-icon>
            <span>个人财务管理</span>
          </div>
          <el-menu 
            mode="horizontal" 
            :default-active="$route.path"
            router
            class="header-menu"
          >
            <el-menu-item index="/reports">
              <el-icon><DataAnalysis /></el-icon>
              <span>报表</span>
            </el-menu-item>
            <el-menu-item index="/transaction/new?mode=ai">
              <el-icon><MagicStick /></el-icon>
              <span>AI记账</span>
            </el-menu-item>
            <el-menu-item index="/transactions">
              <el-icon><List /></el-icon>
              <span>交易记录</span>
            </el-menu-item>
            <el-menu-item index="/accounts">
              <el-icon><CreditCard /></el-icon>
              <span>账户管理</span>
            </el-menu-item>
            <el-menu-item index="/categories">
              <el-icon><Menu /></el-icon>
              <span>分类管理</span>
            </el-menu-item>
            <el-menu-item index="/imports">
              <el-icon><Upload /></el-icon>
              <span>账单导入</span>
            </el-menu-item>
          </el-menu>
          
          <!-- 用户信息 -->
          <div class="user-info">
            <el-dropdown @command="handleUserCommand">
              <span class="user-dropdown">
                <el-icon><User /></el-icon>
                {{ user?.username || '用户' }}
                <el-icon class="el-icon--right"><ArrowDown /></el-icon>
              </span>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item command="profile">
                    <el-icon><Setting /></el-icon>
                    个人设置
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
            <el-button
              class="logout-button"
              type="danger"
              plain
              size="small"
              @click="handleLogout"
            >
              <el-icon><SwitchButton /></el-icon>
              <span>退出</span>
            </el-button>
          </div>
        </div>
      </el-header>

      <!-- 主体内容 -->
      <el-main class="app-main">
        <router-view />
      </el-main>

      <!-- 手机端底部导航 -->
      <nav class="mobile-bottom-nav">
        <router-link to="/reports" class="bottom-nav-item" :class="{ active: $route.path === '/reports' }">
          <el-icon><DataAnalysis /></el-icon>
          <span>报表</span>
        </router-link>
        <router-link to="/transaction/new?mode=ai" class="bottom-nav-item" :class="{ active: $route.path === '/transaction/new' && $route.query.mode === 'ai' }">
          <el-icon><MagicStick /></el-icon>
          <span>AI记账</span>
        </router-link>
        <router-link to="/accounts" class="bottom-nav-item" :class="{ active: $route.path === '/accounts' }">
          <el-icon><CreditCard /></el-icon>
          <span>账户</span>
        </router-link>
        <div class="bottom-nav-item" :class="{ active: showMoreMenu }" @click="showMoreMenu = !showMoreMenu">
          <el-icon><MoreFilled /></el-icon>
          <span>更多</span>
        </div>
      </nav>

      <!-- 更多菜单浮层 -->
      <transition name="fade">
        <div v-if="showMoreMenu" class="more-overlay" @click="showMoreMenu = false">
          <transition name="slide-up">
            <div class="more-sheet" @click.stop>
              <div class="more-sheet-handle"></div>
              <div class="more-grid">
                <router-link to="/transactions" class="more-item" @click="showMoreMenu = false">
                  <div class="more-item-icon"><el-icon><List /></el-icon></div>
                  <span>交易记录</span>
                </router-link>
                <router-link to="/categories" class="more-item" @click="showMoreMenu = false">
                  <div class="more-item-icon"><el-icon><Menu /></el-icon></div>
                  <span>分类管理</span>
                </router-link>
                <router-link to="/imports" class="more-item" @click="showMoreMenu = false">
                  <div class="more-item-icon"><el-icon><Upload /></el-icon></div>
                  <span>账单导入</span>
                </router-link>
              </div>
              <div class="more-sheet-footer">
                <div class="more-logout" @click="handleLogout; showMoreMenu = false">
                  <el-icon><SwitchButton /></el-icon>
                  <span>退出登录</span>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </transition>
    </el-container>
  </div>
</template>

<script>
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  Money, House, List, CreditCard, Plus, User,
  ArrowDown, Setting, SwitchButton, Menu, DataAnalysis, Upload, MagicStick, MoreFilled
} from '@element-plus/icons-vue'
import api from './api'

export default {
  name: 'App',
  components: {
    Money, House, List, CreditCard, Plus, User,
    ArrowDown, Setting, SwitchButton, Menu, DataAnalysis, Upload, MagicStick, MoreFilled
  },
  setup() {
    const store = useStore()
    const router = useRouter()

    const isAuthenticated = computed(() => store.state.isAuthenticated)
    const user = computed(() => store.state.user)
    const showMoreMenu = ref(false)
    
    const handleUserCommand = async (command) => {
      switch (command) {
        case 'profile':
          // 跳转到个人设置页面（暂时用消息提示）
          ElMessage.info('个人设置功能开发中')
          break
        case 'logout':
          await handleLogout()
          break
      }
    }
    
    const handleLogout = async () => {
      try {
        await ElMessageBox.confirm(
          '确定要退出登录吗？',
          '退出确认',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        
        // 调用退出API（可选）
        try {
          await api.logout()
        } catch (error) {
          console.log('服务端退出失败，但仍会清理本地状态')
        }
        
        // 清理本地状态
        store.commit('LOGOUT')
        
        ElMessage.success('已退出登录')
        
        // 跳转到登录页
        router.push('/login')
        
      } catch (error) {
        if (error === 'cancel') {
          return
        }
        console.error('退出登录失败:', error)
      }
    }
    
    return {
      isAuthenticated,
      user,
      showMoreMenu,
      handleUserCommand,
      handleLogout
    }
  }
}
</script>

<style>
.app-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.app-header {
  background-color: #fff;
  border-bottom: 1px solid #e4e7ed;
  padding: 0;
  flex-shrink: 0;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
}

/* 用户信息样式 */
.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
  padding-left: 20px;
}

.user-dropdown {
  display: flex;
  align-items: center;
  gap: 8px;
  color: white;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 6px;
  transition: background-color 0.3s;
}

.user-dropdown:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.user-dropdown .el-icon {
  font-size: 16px;
}

.logout-button .el-icon {
  margin-right: 4px;
}

.logo {
  display: flex;
  align-items: center;
  font-size: 20px;
  font-weight: bold;
  color: #409eff;
  white-space: nowrap;
}

.logo .el-icon {
  margin-right: 8px;
  font-size: 24px;
}

.header-menu {
  border-bottom: none;
  flex: 1;
  justify-content: flex-end;
  min-width: 0;
}

.app-main {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  flex: 1;
  overflow-y: auto;
  width: 100%;
  box-sizing: border-box;
}

/* 响应式导航设计 */

/* 大屏幕 - 桌面端 (>1200px) */
@media (min-width: 1201px) {
  .header-content {
    padding: 0 40px;
  }
  
  .app-main {
    padding: 30px 40px;
  }
  
  .logo {
    font-size: 22px;
  }
  
  .logo .el-icon {
    font-size: 26px;
  }
  
  .header-menu .el-menu-item {
    padding: 0 24px;
    font-size: 16px;
  }
}

/* 中等屏幕 - 平板横屏 (769px - 1200px) */
@media (min-width: 769px) and (max-width: 1200px) {
  .header-content {
    padding: 0 20px;
  }
  
  .app-main {
    padding: 20px;
  }
  
  .header-menu .el-menu-item {
    padding: 0 20px;
  }
}

/* 小屏幕 - 平板竖屏 (481px - 768px) */
@media (min-width: 481px) and (max-width: 768px) {
  .header-content {
    padding: 0 15px;
  }
  
  .app-main {
    padding: 15px;
  }
  
  .logo {
    font-size: 18px;
  }
  
  .logo .el-icon {
    font-size: 22px;
  }
  
  .header-menu .el-menu-item {
    padding: 0 16px;
    font-size: 14px;
  }
  
  .header-menu .el-menu-item span {
    display: none;
  }
  
  .header-menu .el-menu-item .el-icon {
    margin-right: 0;
    font-size: 18px;
  }
}

/* 超小屏幕 - 手机 (≤480px) */
@media (max-width: 480px) {
  .app-header {
    height: 50px;
  }
  
  .header-content {
    padding: 0 12px;
  }
  
  .app-main {
    padding: 12px;
  }
  
  .logo {
    font-size: 16px;
  }
  
  .logo span {
    display: none;
  }
  
  .logo .el-icon {
    font-size: 24px;
    margin-right: 0;
  }
  
  .header-menu {
    min-width: 0;
    overflow-x: auto;
    overflow-y: hidden;
    justify-content: flex-start;
    scrollbar-width: none;
  }

  .header-menu::-webkit-scrollbar {
    display: none;
  }
  
  .header-menu .el-menu-item {
    flex-shrink: 0;
    padding: 0 7px;
    min-width: 40px;
    font-size: 12px;
  }
  
  .header-menu .el-menu-item span {
    display: none;
  }
  
  .header-menu .el-menu-item .el-icon {
    margin-right: 0;
    font-size: 16px;
  }

  .user-info {
    gap: 8px;
    padding-left: 8px;
    margin-left: 0;
    flex-shrink: 0;
  }

  .user-dropdown {
    display: none;
  }

  .logout-button {
    padding: 6px 8px;
  }

  .logout-button .el-icon {
    margin-right: 0;
  }
}

/* 超窄屏幕适配 (≤320px) */
@media (max-width: 320px) {
  .header-content {
    padding: 0 8px;
  }
  
  .header-menu .el-menu-item {
    padding: 0 5px;
    min-width: 36px;
  }
  
  .header-menu .el-menu-item .el-icon {
    font-size: 14px;
  }
}

/* 横屏模式适配 */
@media (orientation: landscape) and (max-height: 500px) {
  .app-header {
    height: 45px;
  }
  
  .logo {
    font-size: 16px;
  }
  
  .logo .el-icon {
    font-size: 20px;
  }
  
  .header-menu .el-menu-item {
    padding: 0 12px;
    font-size: 13px;
  }
  
  .app-main {
    padding: 10px 20px;
  }
}

/* 触摸设备优化 */
@media (hover: none) and (pointer: coarse) {
  .header-menu .el-menu-item {
    min-height: 44px;
    min-width: 44px;
  }
}

/* 深色模式适配 */
@media (prefers-color-scheme: dark) {
  .app-header {
    background-color: #2d2d2d;
    border-bottom-color: #404040;
  }
  
  .logo {
    color: #409eff;
  }
  
  .header-menu .el-menu-item {
    color: #ffffff;
  }
  
  .header-menu .el-menu-item:hover {
    background-color: #363636;
  }
  
  .header-menu .el-menu-item.is-active {
    background-color: #409eff;
    color: #ffffff;
  }
}

/* 高分辨率屏幕适配 */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  .logo .el-icon,
  .header-menu .el-menu-item .el-icon {
    backface-visibility: hidden;
    transform: translateZ(0);
  }
}

/* 无障碍访问 - 减少动画 */
@media (prefers-reduced-motion: reduce) {
  .header-menu .el-menu-item {
    transition: none;
  }
}

/* ── 手机端底部导航 ─────────────────────────────────────── */
.mobile-bottom-nav {
  display: none;
}

@media (max-width: 768px) {
  /* 隐藏顶部菜单和用户信息，只保留 logo */
  .header-menu,
  .user-info {
    display: none !important;
  }

  .app-header {
    height: 44px;
  }

  .header-content {
    justify-content: flex-start;
    padding: 0 16px;
  }

  /* 主内容底部留出导航栏高度 */
  .app-main {
    padding-bottom: calc(60px + env(safe-area-inset-bottom));
  }

  /* 底部导航栏 */
  .mobile-bottom-nav {
    display: flex;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: calc(56px + env(safe-area-inset-bottom));
    padding-bottom: env(safe-area-inset-bottom);
    background: #fff;
    border-top: 1px solid #e4e7ed;
    z-index: 1000;
    box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.08);
  }

  .bottom-nav-item {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 3px;
    color: #909399;
    text-decoration: none;
    font-size: 11px;
    cursor: pointer;
    transition: color 0.2s;
    -webkit-tap-highlight-color: transparent;
  }

  .bottom-nav-item .el-icon {
    font-size: 22px;
  }

  .bottom-nav-item.active,
  .bottom-nav-item.router-link-active {
    color: #409eff;
  }

  /* 更多浮层遮罩 */
  .more-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 1001;
  }

  /* 底部弹出菜单 */
  .more-sheet {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: #fff;
    border-radius: 16px 16px 0 0;
    padding: 8px 20px calc(20px + env(safe-area-inset-bottom));
  }

  .more-sheet-handle {
    width: 36px;
    height: 4px;
    background: #dcdfe6;
    border-radius: 2px;
    margin: 8px auto 16px;
  }

  .more-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 8px;
  }

  .more-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    text-decoration: none;
    color: #303133;
    font-size: 12px;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  .more-item-icon {
    width: 52px;
    height: 52px;
    border-radius: 14px;
    background: #f5f7fa;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .more-item-icon .el-icon {
    font-size: 22px;
    color: #409eff;
  }

  .more-sheet-footer {
    border-top: 1px solid #f0f2f5;
    padding-top: 12px;
    margin-top: 4px;
  }

  .more-logout {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px;
    color: #f56c6c;
    font-size: 14px;
    cursor: pointer;
    border-radius: 8px;
  }

  .more-logout .el-icon {
    font-size: 18px;
  }

  /* 动画 */
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s;
  }
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  .slide-up-enter-active,
  .slide-up-leave-active {
    transition: transform 0.25s ease;
  }
  .slide-up-enter-from,
  .slide-up-leave-to {
    transform: translateY(100%);
  }
}
</style> 
